import React from 'react';
import { ExternalLink } from 'lucide-react';

/**
 * PortfolioGrid — 3-up grid of real Audio & Co. builds.
 *
 * Two "kinds": `client` (built for someone else) and `in-house` (Jo's own
 * businesses, ie. we eat our own dog food). Screenshots are captured
 * once and served as static assets from /public/portfolio/*.jpg so we
 * don't hit third-party screenshot services on every page load.
 */
const ITEMS = [
  {
    id: 'dimple',
    kind: 'client',
    name: 'The Dimple Method\u2122',
    href: 'https://dimplemethod.com/',
    img: '/portfolio/dimple/home.jpg',
    alt: 'The Dimple Method homepage, a business website built for SEO by Audio & Co. with podcast, YouTube, book and AI chatbot',
    title: 'The Dimple Method | Built by Audio & Co.',
    tagline: 'Full Pro Plan build. Website, podcast, YouTube, book and more.',
    body: 'Business website on her own domain, top 5% global podcast, YouTube managed end-to-end, 70+ articles with audio, a trained AI chatbot, two programmes, her book formatted, audiobook recorded and a companion journal.',
    what: ['Pro Plan client', 'Website + podcast', 'Book + audiobook'],
  },
  {
    id: 'andrews',
    kind: 'client',
    name: 'Andrews Building Company',
    href: 'https://andrewsbuilding.com/',
    img: '/portfolio/andrews.jpg',
    alt: 'Andrews Building Company homepage, a commercial refurbishment website designed and built for SEO by Audio & Co.',
    title: 'Andrews Building Company | Built by Audio & Co.',
    tagline: 'Full-service brand refresh, site build and content system.',
    body: 'Industrial and commercial refurbishment specialists established in 1986. Full site rebuild with editorial hero, service architecture, portfolio module and enquiry flows — built to convert commercial buyers, not just look pretty.',
    what: ['Custom design', 'Site build', 'Content architecture'],
  },
  {
    id: 'bsf',
    kind: 'client',
    name: 'The British Sustainable Floristry Academy',
    href: 'https://www.britishsustainablefloristryacademy.com/',
    img: '/portfolio/bsf.jpg?v=2',
    alt: 'The British Sustainable Floristry Academy homepage, a course platform and website built for SEO by Audio & Co.',
    title: 'British Sustainable Floristry Academy | Audio & Co.',
    tagline: 'Custom course platform, brand and site.',
    body: 'Built from scratch by Audio & Co. Elegant editorial design, course-delivery infrastructure, and content architecture that scales with the school. British Flowers. Sustainable Techniques. Inspired Futures.',
    what: ['Custom design', 'Course platform', 'Brand & site'],
  },
  {
    id: 'tyb',
    kind: 'client',
    name: 'Treat Your Business (Thrive)',
    href: 'https://thrive-businesscoaching.com/',
    img: '/portfolio/tyb.jpg?v=2',
    alt: 'Treat Your Business by Thrive homepage, a podcast, YouTube and business website managed by Audio & Co.',
    title: 'Treat Your Business | Audio & Co. client',
    tagline: 'Pro Plan client. Podcast, YouTube and business website.',
    body: 'Weekly done-for-you production for Katie Bell\u2019s business coaching brand for physio, osteopathy, sports therapy and podiatry clinic owners. Podcast, YouTube long-form, Shorts and a business website, all shipped consistently to 500+ clinic owners worldwide.',
    what: ['Pro Plan client', 'Podcast + YouTube', 'Business website'],
  },
  {
    id: 'popcrown',
    kind: 'in-house',
    name: 'PopCrown',
    href: 'https://popcrownofficial.com/',
    img: '/portfolio/popcrown.jpg',
    alt: 'PopCrown homepage, a multilingual progressive web app designed, built and grown in-house by Audio & Co.',
    title: 'PopCrown PWA | Built by Audio & Co.',
    tagline: 'PWA reaching 130+ languages. Built and grown by Jo.',
    body: 'The Michael Jackson fan app. Progressive web app with community features, real-time countdowns, daily quizzes and event ticketing. Jo\u2019s own product, running in production.',
    what: ['PWA / mobile web', '130+ languages', 'Community + events'],
  },
  {
    id: 'brunchi',
    kind: 'in-house',
    name: 'Brunchi.shop',
    href: 'https://brunchi.shop/',
    img: '/portfolio/brunchi.jpg',
    alt: 'Brunchi.shop homepage, a Lanzarote wine and spirits e-commerce website built for SEO by Audio & Co.',
    title: 'Brunchi.shop | Built by Audio & Co.',
    tagline: 'Jo\u2019s e-commerce brand. Built and run in-house.',
    body: 'Lanzarote wine, craft beer & spirits, sold online and delivered locally. Custom age-verification, WhatsApp ordering flow, and a spirit-forward brand identity.',
    what: ['E-commerce site', 'WhatsApp ordering', 'Local delivery'],
  },
];

function Card({ item }) {
  const tag = item.kind === 'client' ? 'Client build' : 'In-house build (Jo\u2019s own)';
  const tagBg = item.kind === 'client' ? 'bg-[#BC516F]' : 'bg-[#1E2A44]';
  return (
    <article
      className="bg-white border border-[#E2E8F0] rounded-[14px] overflow-hidden flex flex-col shadow-[0_6px_18px_rgba(30,42,68,0.06)] hover:shadow-[0_18px_44px_rgba(30,42,68,0.12)] transition"
      data-testid={`portfolio-card-${item.id}`}
    >
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className="relative block aspect-[16/10] overflow-hidden bg-[#F6F8FB]"
        aria-label={`Visit ${item.name}`}
      >
        <img
          src={item.img}
          alt={item.alt}
          title={item.title}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-top transition duration-500 hover:scale-[1.03]"
        />
        <span className={`absolute top-3 left-3 text-white text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${tagBg}`}>
          {tag}
        </span>
      </a>
      <div className="p-5 sm:p-6 flex-1 flex flex-col">
        <h3 className="font-display font-semibold text-[#1E2A44] text-lg leading-tight">{item.name}</h3>
        <p className="text-[#BC516F] text-sm font-medium mt-1">{item.tagline}</p>
        <p className="text-[#475569] text-[15px] leading-relaxed mt-3 flex-1">{item.body}</p>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {item.what.map((w) => (
            <span key={w} className="text-[11px] font-medium text-[#475569] bg-[#F6F8FB] border border-[#E2E8F0] rounded-full px-2.5 py-1">{w}</span>
          ))}
        </div>
        <a
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex items-center gap-1.5 text-[#BC516F] hover:text-[#A7468A] font-semibold text-sm"
          data-testid={`portfolio-visit-${item.id}`}
        >
          Visit live site <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </article>
  );
}

export default function PortfolioGrid({ eyebrow = 'Recent builds', heading, subheading, testId = 'portfolio-grid' }) {
  return (
    <div data-testid={testId}>
      {(eyebrow || heading || subheading) && (
        <div className="max-w-3xl">
          {eyebrow && <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">{eyebrow}</p>}
          {heading && <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">{heading}</h2>}
          {subheading && <p className="text-[#475569] mt-4 leading-relaxed text-[17px]">{subheading}</p>}
        </div>
      )}
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {ITEMS.map((it) => <Card key={it.id} item={it} />)}
      </div>
    </div>
  );
}

export { ITEMS as PORTFOLIO_ITEMS };
